import classNames from 'classnames';
import React, { useState } from 'react';

import { getZonedDate } from '@eika-infrastruktur/date';
import { AttentionLabel } from '@eika/label';

import { useConfig } from '../../../../defaultConfig';
import { useTransferAccountsContext } from '../../../../hooks/useAccountState';
import useTranslation from '../../../../hooks/useTranslation';
import { NoteTypes } from '../../../../translations/translationType';
import {
    AmountInput,
    DateSelect,
    FromAccountSelect,
    OwntextInput,
} from '../../../NettbankCreateTransactions/components';
import { MessageInput } from '../../../fields';
import { AccountFreeWithdrawalLabel } from '../../../shared/AccountFreeWithdrawalLabel';
import DesktopDropdownPlaceholder from '../../DesktopDropdownPlaceholder';
import { SetAccountType, TransferActionType } from '../form/actions';
import { isNoCoverage } from '../form/validate';
import useToAccount from '../hooks/useToAccount';
import FieldProps from './FieldProps';

const TransferDesktopFields: React.FC<FieldProps> = ({
    form,
    locked,
    freeWithdrawals,
    date,
    allAccounts,
    validFromAccounts,
    invalidFromAccounts,
    accountState,
    isActive = true,
    useDesktopPlaceholder = true,
    isFocusModal,
}) => {
    const { state, errors, handleChange } = form;
    const translated = useTranslation();
    const { featureToggles } = useConfig();
    const { targetFromAccount, targetToAccount: toAccountInState } = useTransferAccountsContext();
    const { toAccounts, selectedToAccount, isUnknownAccount } = useToAccount(state, toAccountInState, allAccounts);

    const [placeholderHeight, setPlaceholderHeight] = useState(0);

    return (
        <>
            <FromAccountSelect
                className={classNames('transaction-form__from-account-selector', {
                    error: errors.accountId || errors.toAccount || isUnknownAccount,
                    'has-withdrawal': freeWithdrawals.show,
                    open: isActive,
                })}
                id="transfer-from-account"
                label={translated.from}
                items={validFromAccounts ?? []}
                invalidItems={invalidFromAccounts}
                selected={state.fromAccount}
                selectedAccount={targetFromAccount}
                onSelect={(accountNumber) => {
                    handleChange({
                        type: TransferActionType.ACCOUNT_SELECTED,
                        payload: {
                            setAccountType: SetAccountType.SET_FROM_ACCOUNT,
                            accountNumber,
                        },
                    });
                }}
                placeholder={translated.chooseAccount}
                locked={locked}
                isLoading={accountState.isLoading}
                error={
                    errors.accountId
                        ? { className: 'transaction-form__from-account-error', message: errors.accountId }
                        : undefined
                }
                isPaymentModalOpen
                bottomLabel={
                    freeWithdrawals.show ? (
                        <AccountFreeWithdrawalLabel
                            className="transaction-form__from-account-withdrawal"
                            freeWithdrawals={freeWithdrawals.data}
                        />
                    ) : undefined
                }
                setPlaceholderHeight={setPlaceholderHeight}
                tabIndex={isActive ? undefined : -1}
            />
            <FromAccountSelect
                className={classNames('transaction-form__to-account-selector', {
                    error: errors.toAccount || errors.accountId || isUnknownAccount,
                    'has-withdrawal': freeWithdrawals.show,
                    open: isActive,
                })}
                id="transfer-to-account"
                label={translated.to}
                items={toAccounts ?? []}
                selected={state.toAccount}
                selectedAccount={selectedToAccount}
                onSelect={(accountNumber) => {
                    handleChange({
                        type: TransferActionType.ACCOUNT_SELECTED,
                        payload: {
                            setAccountType: SetAccountType.SET_TO_ACCOUNT,
                            accountNumber,
                        },
                    });
                }}
                placeholder={translated.chooseAccount}
                locked={locked}
                isLoading={accountState.isLoading}
                error={
                    errors.toAccount
                        ? { className: 'transaction-form__to-account-error', message: errors.toAccount }
                        : undefined
                }
                isPaymentModalOpen
                setPlaceholderHeight={setPlaceholderHeight}
                bottomLabel={
                    isUnknownAccount && !errors.toAccount ? (
                        <AttentionLabel
                            className="transaction-form__to-account-unknown"
                            text={translated.unknownAccountWarning}
                        />
                    ) : undefined
                }
                tabIndex={isActive ? undefined : -1}
            />
            {isActive && (
                <>
                    <AmountInput
                        className={classNames('transaction-form__amount-selector', { error: errors.amount })}
                        value={state.amount}
                        label={translated.payAmount}
                        error={errors.amount}
                        onChange={(value) => handleChange({ type: TransferActionType.AMOUNT, payload: value })}
                        isNoCoverage={isNoCoverage(state, targetFromAccount)}
                        locked={locked}
                        currency={targetFromAccount?.currency}
                    />
                    <MessageInput
                        label={translated.amountNote[NoteTypes.MESSAGE].label}
                        placeholder={translated.amountNote[NoteTypes.MESSAGE].placeholder}
                        className={classNames('transaction-form__message-selector', { error: errors.note })}
                        noteValue={state.note}
                        error={errors.note}
                        selectedReceiver={selectedToAccount}
                        onChange={(value) => handleChange({ type: TransferActionType.NOTE, payload: value })}
                        locked={locked}
                        disableKid
                    />
                    <DateSelect
                        className={classNames('transaction-form__date-selector', { error: errors.transferDate })}
                        date={state.transferDate ?? getZonedDate()}
                        minDate={date.minDate}
                        onChange={(date) => handleChange({ type: TransferActionType.TRANSFER_DATE, payload: date })}
                        error={errors.transferDate}
                        locked={locked}
                        useFocusModal={isFocusModal}
                    />
                    {!(featureToggles.disableOwnText || featureToggles.disableOwnTransferText) && (
                        <OwntextInput
                            className="transaction-form__owntext-selector"
                            ownText={state.ownText}
                            locked={locked}
                            label={translated.transferAddOwnText}
                            onChangeOwntext={(value) =>
                                handleChange({
                                    type: TransferActionType.OWN_TEXT,
                                    payload: value,
                                })
                            }
                        />
                    )}
                    {useDesktopPlaceholder && <DesktopDropdownPlaceholder height={placeholderHeight} />}
                </>
            )}
        </>
    );
};

export default TransferDesktopFields;
